import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import { equals, filter, isEmpty } from 'ramda'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import {
  Chip,
  FormControl,
  Popover,
  Typography,
  makeStyles,
  Grid,
  FormGroup,
  Icon,
  FormControlLabel,
} from '@material-ui/core'

import CheckBox from 'app/componentV4/CheckBox'

const useStyles = makeStyles((theme) => ({
  chip: {
    border: `1px solid ${theme.colors['primary-200']}`,
    '&.MuiChip-clickable:hover': {
      border: `1px solid ${theme.colors['primary-350']}`,
    },
  },
}))

const CheckboxSelectorV2 = (props) => {
  const {
    name,
    label,
    value,
    options,
    onChange,
    disabled,
    className,
    isSelectAll = false,
    isOneSelected = false,
  } = props
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const [isOpen, setIsOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selected, setSelected] = useState(value)

  const textAll = formatMessage({
    id: 'statuse.all',
  })

  const handleOneSelectedLabel = useCallback(
    (selected) => {
      if (selected.length === 0) return textAll
      else
        return formatMessage({
          id: selected[0]?.label,
        })
    },
    [formatMessage, textAll],
  )

  const handleLabel = useCallback(
    (list) => {
      if (isOneSelected) return handleOneSelectedLabel(list)

      if (list.length === options.length || list.length === 0) return textAll
      else
        return list.length === 1
          ? formatMessage(
              {
                id: list[0]?.label,
              },
              list[0]?.valueLabel,
            )
          : `${formatMessage(
              {
                id: list[0]?.label,
              },
              list[0]?.valueLabel,
            )},...`
    },
    [
      formatMessage,
      handleOneSelectedLabel,
      isOneSelected,
      options.length,
      textAll,
    ],
  )

  const handleOnChange = (checked, object) => {
    setSelected((prev) => {
      let state = []
      if (checked) state = [...prev, object]
      else state = filter((list) => list.id !== object.id)(prev)

      state.sort((a, b) => a.id - b.id)
      return state
    })
  }

  const handleSelectedAll = (bool) => {
    let state = []
    state = bool ? options : []

    setSelected(state)
  }

  const handleOnClose = useCallback(() => {
    !equals(value, selected) && onChange(selected)
    setIsOpen(false)
    setAnchorEl(null)
  }, [onChange, selected, value])

  useEffect(() => {
    setSelected(value)
  }, [value])

  return (
    <>
      {isEmpty(options) ? null : (
        <>
          <Chip
            className={clsx(classes.chip, className)}
            label={
              <Typography className="flex items-center">
                {label}:
                <strong className="mx-3">{handleLabel(selected)}</strong>
                <Icon fontSize="small" className="text-primary">
                  {isOpen ? 'expand_less' : 'expand_more'}
                </Icon>
              </Typography>
            }
            onClick={(event) => {
              setIsOpen(true)
              setAnchorEl(event.currentTarget)
            }}
            disabled={disabled}
          />

          <Popover
            id="filter-timeline-type"
            open={isOpen}
            anchorEl={anchorEl}
            onClose={() => handleOnClose()}
            className="w-full"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Grid item xs={12} md={3}>
              <FormControl
                name={name}
                component="fieldset"
                className={clsx(classes.a, 'p-5')}
              >
                <FormGroup>
                  {isSelectAll && (
                    <div className="whitespace-nowrap text-primary text-14 font-normal">
                      <FormControlLabel
                        control={
                          <CheckBox
                            checked={selected.length === options.length}
                            onChange={(e) => {
                              handleSelectedAll(e.target.checked)
                            }}
                            indeterminate={
                              selected.length > 0 &&
                              selected.length !== options.length
                            }
                            name={`${name}-select-all-checkbox`}
                            disabled={disabled}
                          />
                        }
                        label={formatMessage(
                          {
                            id: 'all.label',
                          },
                          { label: label },
                        )}
                      />
                    </div>
                  )}
                  {options.map((list, index) => {
                    return (
                      <div
                        key={index}
                        className="whitespace-nowrap text-primary text-14 font-normal"
                      >
                        <FormControlLabel
                          control={
                            <CheckBox
                              checked={selected.some(
                                ({ id }) => id === list.id,
                              )}
                              onChange={(e) => {
                                handleOnChange(e.target.checked, list)
                              }}
                              name={`${list.name}-checkbox`}
                              disabled={disabled}
                            />
                          }
                          label={formatMessage(
                            {
                              id: list.label,
                            },
                            list.valueLabel,
                          )}
                        />
                      </div>
                    )
                  })}
                </FormGroup>
              </FormControl>
            </Grid>
          </Popover>
        </>
      )}
    </>
  )
}

const valuePropTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
}
CheckboxSelectorV2.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  isSelectAll: PropTypes.bool,
  className: PropTypes.string,
  isOneSelected: PropTypes.bool,
  value: PropTypes.arrayOf(PropTypes.shape(valuePropTypes)),
  options: PropTypes.arrayOf(PropTypes.shape(valuePropTypes)).isRequired,
}

export default CheckboxSelectorV2
